import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { fetchApi } from '../api/client';
import { getComplexityLevel } from '../utils/complexity';
import type { BookkeepingRecord, Appointment, BookkeepingStatusCategory } from '../types';

// Extended BookkeepingRecord with filtered task counts + derived status
export interface BookkeepingRecordWithFilteredTasks extends BookkeepingRecord {
  filteredOutstandingTasks: number;
  filteredCompletedTasks: number;
  taskCountPeriod: string;
  /** First day of the task window, `yyyy-MM-dd`; the UI words it. Absent from an API that predates it. */
  taskCountSince?: string | null;
  statusCategory: BookkeepingStatusCategory;
}

// ---------- Backend contract (api/Models/ApiResponse.cs ClientSummary/ClientListResponse) ----------

interface ClientSummaryDto {
  bookkeepingId: string;
  name: string;
  clientId?: string | null;
  clientName: string;
  bookkeeperName: string;
  bookkeeperId?: string | null;
  avgMonthlyTransactions: number;
  complexity: 'low' | 'medium' | 'high';
  qtrReviewDone: boolean;
  transactionLockDate?: string | null;
  goodStanding: boolean;
  outstandingTasks: number;
  completedTasks: number;
  waitingOnClientTasks: number;
  filteredOutstandingTasks: number;
  filteredCompletedTasks: number;
  taskCountPeriod: string;
  taskCountSince?: string | null;
  statusCategory: BookkeepingStatusCategory;
  generalNotes?: string | null;
  xeroLink?: string | null;
  salesTax: boolean;
}

interface ClientListResponseDto {
  clients: ClientSummaryDto[];
  totalCount: number;
  stats: unknown;
  syncFreshness?: TaskSyncFreshness;
}

/**
 * How current the ptc-core SQL mirror behind the task counts is.
 *
 * Shipped 2026-08-28: that mirror's feed stopped on 2026-07-31 and 52 clients' open-task counts
 * were wrong with nothing on screen saying so. A frozen snapshot renders identically to live data,
 * which is the whole problem this type exists to make visible.
 *
 * `known: false` means the watermark could not be read — deliberately DISTINCT from `isStale`, and
 * deliberately still banner-worthy: a feed we cannot measure must not render as a healthy one.
 */
export interface TaskSyncFreshness {
  known: boolean;
  lastSyncedAt: string | null;
  ageHours: number;
  isStale: boolean;
}

/** Absent `syncFreshness` (older API build) reads as unknown, never as fresh. */
const UNKNOWN_FRESHNESS: TaskSyncFreshness = {
  known: false,
  lastSyncedAt: null,
  ageHours: 0,
  isStale: true,
};

// Adapts the scoped API's ClientSummary shape back into the Dataverse-odata-styled
// BookkeepingRecordWithFilteredTasks shape the existing pages already read from (cr8f7_* field
// names, formatted-value annotations). Keeps this hook's return type — and every consuming page —
// unchanged while the actual read moves from a browser-side Dataverse call to GET /api/clients
// (ClientFunctions.GetClients, which honors PER_CALLER_SCOPING).
function toBookkeepingRecord(c: ClientSummaryDto): BookkeepingRecordWithFilteredTasks {
  return {
    cr8f7_bookkeepingid: c.bookkeepingId,
    cr8f7_name: c.name,
    _cr8f7_client_value: c.clientId ?? '',
    _ownerid_value: c.bookkeeperId ?? '',
    '_cr8f7_client_value@OData.Community.Display.V1.FormattedValue': c.clientName,
    '_ownerid_value@OData.Community.Display.V1.FormattedValue': c.bookkeeperName,
    cr8f7_avgmonthlytransactions: c.avgMonthlyTransactions,
    cr8f7_qtrreviewdone: c.qtrReviewDone,
    cr8f7_transactionlockdatexero: c.transactionLockDate ?? undefined,
    cr8f7_goodstanding: c.goodStanding,
    cr8f7_completedtask: c.completedTasks,
    cr8f7_outstandingtask: c.outstandingTasks,
    cr8f7_waitingonclienttask: c.waitingOnClientTasks,
    cr8f7_generalnotes: c.generalNotes ?? undefined,
    cr8f7_xerolink: c.xeroLink ?? undefined,
    cr8f7_salestax: c.salesTax,
    statecode: 0, // GET /clients already filters to statecode eq 0
    statuscode: 0,
    createdon: '',
    modifiedon: '',
    _createdby_value: '',
    _modifiedby_value: '',
    filteredOutstandingTasks: c.filteredOutstandingTasks,
    filteredCompletedTasks: c.filteredCompletedTasks,
    taskCountPeriod: c.taskCountPeriod,
    taskCountSince: c.taskCountSince ?? null,
    statusCategory: c.statusCategory,
  };
}

/**
 * Shared fetcher for the scoped clients list — used by the hook AND the prefetcher.
 *
 * Returns the whole envelope rather than just the records so `useTaskSyncFreshness` can read the
 * mirror watermark off the SAME cache entry. Consumers keep their old shape via `select` below;
 * the banner therefore costs zero extra requests.
 */
async function fetchBookkeepingClients(): Promise<{
  records: BookkeepingRecordWithFilteredTasks[];
  syncFreshness: TaskSyncFreshness;
}> {
  const res = await fetchApi<ClientListResponseDto>('/clients');
  return {
    records: res.clients.map(toBookkeepingRecord),
    syncFreshness: res.syncFreshness ?? UNKNOWN_FRESHNESS,
  };
}

// Routed through the scoped func API (GET /clients → ClientFunctions.GetClients) instead of a
// browser-side Dataverse read — closes the per-caller-scoping bypass described in the 2026-07-03
// supervision audit finding #1 (Dashboard/Clients/Client-detail/Reviews all consume this hook).
//
// KNOWN OVERLAP (SUP-01, documented + deliberately not consolidated): api/bookkeeping.ts's
// useBookkeepingClients (GET /bookkeeping/clients) fetches a second, different projection of the
// same client set for the Bookkeeping page (task aggregates + filing statuses + next meeting vs
// this hook's Dataverse-shaped record + filtered counts + scoping stats). The endpoints have
// different response contracts, scoping behaviour, and consumers — merging them is a contract
// change, not a cleanup, so both paths stay until the endpoints themselves converge.
export function useBookkeepingClients() {
  return useQuery({
    queryKey: ['bookkeeping-clients'],
    queryFn: fetchBookkeepingClients,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (r) => r.records,
  });
}

/**
 * The mirror-freshness verdict behind the task counts, off the same cache entry as the list.
 *
 * While a request is in flight there is nothing to report yet, so this returns `undefined` rather
 * than a fabricated verdict — a banner that flashes "unknown" on every page load would train staff
 * to ignore it, which is the one failure this interim cannot afford.
 */
export function useTaskSyncFreshness() {
  return useQuery({
    queryKey: ['bookkeeping-clients'],
    queryFn: fetchBookkeepingClients,
    staleTime: 1000 * 60 * 5,
    select: (r) => r.syncFreshness,
  });
}

/**
 * Background/intent prefetch of the clients list (key must match useBookkeepingClients).
 * ClientDetailPage derives from this same list query, so warming it covers detail
 * navigation too. Best-effort: prefetchQuery already swallows errors.
 * Staleness: the shared queryClient's default staleTime (5 min) governs — prefetching fresh data is a no-op.
 */
export function prefetchBookkeepingClients(qc: QueryClient): Promise<void> {
  return qc.prefetchQuery({ queryKey: ['bookkeeping-clients'], queryFn: fetchBookkeepingClients });
}

export function useBookkeepingClient(id: string | undefined) {
  return useQuery({
    queryKey: ['bookkeeping-client', id],
    queryFn: () => {
      if (!id) throw new Error('No ID provided');
      // ClientFunctions.GetClient — per-caller scoped (404s for a non-owner, non-admin caller).
      return fetchApi<BookkeepingRecord>(`/clients/${id}`);
    },
    enabled: !!id,
  });
}

export function useUpcomingAppointments(
  daysAhead: number = 14,
  options?: { taxOnly?: boolean },
) {
  const taxOnly = options?.taxOnly ?? false;
  return useQuery({
    queryKey: ['appointments', 'upcoming', daysAhead, taxOnly],
    queryFn: () =>
      // DashboardFunctions.GetUpcomingAppointments — intentionally org-wide (not per-caller
      // scoped), matching this hook's previous unscoped Dataverse read. taxOnly=true asks the
      // server to keep only tax-related appointments (AppointmentClassifier.IsTaxAppointment).
      fetchApi<Appointment[]>(
        `/dashboard/appointments?days=${daysAhead}${taxOnly ? '&taxOnly=true' : ''}`,
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useClientAppointments(accountId: string | undefined) {
  return useQuery({
    queryKey: ['appointments', 'client', accountId],
    queryFn: () => {
      if (!accountId) throw new Error('No account ID provided');
      // ClientFunctions.GetClientAppointments — per-caller scoped (empty list for a non-owner).
      return fetchApi<Appointment[]>(`/clients/${accountId}/appointments`);
    },
    enabled: !!accountId,
  });
}

export interface ClientWithStats extends BookkeepingRecord {
  upcomingAppointments: Appointment[];
  daysSinceLastReview: number | null;
  complexity: 'low' | 'medium' | 'high';
}

export function useClientsWithStats() {
  const clientsQuery = useBookkeepingClients();
  const appointmentsQuery = useUpcomingAppointments(30);

  const isLoading = clientsQuery.isLoading || appointmentsQuery.isLoading;
  const error = clientsQuery.error || appointmentsQuery.error;

  const data: ClientWithStats[] | undefined = clientsQuery.data?.map(client => {
    const clientAppointments = appointmentsQuery.data?.filter(
      apt => apt._regardingobjectid_value === client._cr8f7_client_value
    ) || [];

    // Use centralized complexity calculation from utils/complexity
    const complexity = getComplexityLevel(client.cr8f7_avgmonthlytransactions);

    return {
      ...client,
      upcomingAppointments: clientAppointments,
      daysSinceLastReview: null, // Will be calculated from Cosmos DB review history
      complexity,
    };
  });

  return {
    data,
    isLoading,
    error,
    refetch: () => {
      clientsQuery.refetch();
      appointmentsQuery.refetch();
    },
  };
}
